import React, { useEffect } from "react";
import { Language, ProductFormText } from "../../types/common.types";
import { FormStateProps } from "./ModalProductUpdate";
import { Box, Switch, TextField, Typography } from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";

interface Props extends FormStateProps {
  languages: Language[];
}

const Name = (props: Props) => {
  const { languages, setFields, formFields } = props;
  const { names } = formFields;

  const nameTranslation = useTranslation("new_product_name");
  const productIsInStock = useTranslation("product_is_in_stock");
  const notInStock = useTranslation("not_in_stock");
  const productWillNotBeVisibleToCustomers = useTranslation(
    "product_will_not_be_visible_to_customers"
  );
  const visibilityTranslation = useTranslation("visibility");

  useEffect(() => {
    const initialLanguages: ProductFormText = {};
    languages.forEach((language) => (initialLanguages[language.iso] = ""));
    setFields({ ...formFields, names: initialLanguages });
    // eslint-disable-next-line
  }, []);

  const onNameChange = (lang: string, value: string) => {
    const productNames = { ...names, [lang]: value };
    setFields({ ...formFields, names: productNames });
  };

  return (
    <>
      <Box sx={{ display: "grid", gap: 1.25 }}>
        {languages.length &&
          languages.map((language, index) => {
            return (
              <Box key={`${language.iso}-${index}`}>
                <TextField
                  id="standard-basic"
                  label={`${nameTranslation} ${language.name}`}
                  value={names[language.iso] || ""}
                  onChange={(e) => onNameChange(language.iso, e.target.value)}
                  size="small"
                  sx={darkFieldSx}
                  required
                />
              </Box>
            );
          })}
      </Box>
      <Box
        sx={{
          mt: 1.25,
          p: 1.25,
          borderRadius: "8px",
          border: "1px solid rgba(131, 60, 65, 0.16)",
          bgcolor: "rgba(31, 22, 21, 0.72)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              color: "#7a6560",
              fontFamily:
                '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
              fontSize: "10px",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            {visibilityTranslation}
          </Typography>
          <Typography sx={{ color: "#f0ece8", fontSize: "12px", mt: 0.3 }}>
            {formFields.inStock ? (
              productIsInStock
            ) : (
              <>
                {notInStock}{" "}
                <Box component="span" sx={{ color: "#7a6560" }}>
                  ({productWillNotBeVisibleToCustomers})
                </Box>
              </>
            )}
          </Typography>
        </Box>
        <Switch
          checked={formFields.inStock}
          onChange={() =>
            setFields({ ...formFields, inStock: !formFields.inStock })
          }
          size="small"
          sx={darkSwitchSx}
        />
      </Box>
    </>
  );
};

export const darkFieldSx = {
  width: "100%",
  "& .MuiInputLabel-root": {
    color: "#7a6560",
    fontSize: "12px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#9e4a50",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "7px",
    bgcolor: "#1f1615",
    color: "#f0ece8",
    fontSize: "13px",
    "& fieldset": {
      borderColor: "rgba(131, 60, 65, 0.18)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(158, 74, 80, 0.45)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#833c41",
    },
  },
  "& .MuiInputBase-input": {
    color: "#f0ece8",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#7a6560",
    opacity: 1,
  },
};

export const darkSwitchSx = {
  width: 38,
  height: 22,
  p: 0,
  "& .MuiSwitch-switchBase": {
    p: "2px",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#f9f3ef",
      "& + .MuiSwitch-track": {
        bgcolor: "#833c41",
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 18,
    height: 18,
    boxShadow: "none",
  },
  "& .MuiSwitch-track": {
    borderRadius: 22,
    bgcolor: "#3a2826",
    opacity: 1,
  },
};

export default Name;
